import React, { useState, useCallback } from 'react';
import { Table, Input, Button } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PartNumberValidation } from '../../../../../types/scorecard';
import { ADOPTION_COLORS } from '../../../../constants/graphColors';
import ValidationStatusTags from './ValidationStatusTags';

export interface PartNumberSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  order?: string;
}

export interface PartNumberPaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface PartNumberBreakdownProps {
  data: PartNumberValidation[];
  loading?: boolean;
  pagination?: PartNumberPaginationInfo;
  onSearch?: (params: PartNumberSearchParams) => void;
  onTableChange?: (pagination: any, filters: any, sorter: any) => void;
}

const PartNumberBreakdown: React.FC<PartNumberBreakdownProps> = ({
  data,
  loading = false,
  pagination,
  onSearch,
  onTableChange,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const defaultPagination = {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  };

  const currentPagination = pagination || defaultPagination;

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (!onSearch) return;

      if (!searchTerm.trim()) {
        onSearch({ page: 1, limit: currentPagination.limit });
        return;
      }

      onSearch({
        search: searchTerm.trim(),
        page: 1,
        limit: currentPagination.limit,
      });
    },
    [onSearch, currentPagination.limit]
  );

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);

    if (value.length >= 3) {
      handleSearch(value);
    } else if (value.length === 0) {
      handleSearch('');
    }
  };

  const handleClearSearch = () => {
    setSearchValue('');
    handleSearch('');
  };

  const columns = [
    {
      title: t('receiver:columns.brand'),
      dataIndex: 'brand',
      key: 'brand',
      width: 120,
      render: (text: string) => (
        <div className="text-xs font-medium truncate" title={text}>
          {text}
        </div>
      ),
    },
    {
      title: t('receiver:tableColumns.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      render: (text: string) => (
        <div className="text-xs font-mono" title={text}>
          {text}
        </div>
      ),
    },
    {
      title: t('receiver:fulfilled'),
      dataIndex: 'fulfilled',
      key: 'fulfilled',
      width: 60,
      align: 'center' as const,
      render: (value: number) => (
        <div className="text-xs font-bold" style={{ color: ADOPTION_COLORS.SUCCESS }}>
          {value}
        </div>
      ),
    },
    {
      title: t('receiver:notFulfilled'),
      dataIndex: 'notFulfilled',
      key: 'notFulfilled',
      width: 80,
      align: 'center' as const,
      render: (value: number) => (
        <div className="text-xs font-bold" style={{ color: ADOPTION_COLORS.ERROR }}>
          {value}
        </div>
      ),
    },
    {
      title: t('receiver:validationsMissing'),
      dataIndex: 'validationsMissing',
      key: 'validationsMissing',
      render: (validations: string[]) => <ValidationStatusTags validations={validations} />,
    },
  ];

  if (!data && !searchValue.trim() && currentPagination.total === 0) {
    return (
      <div
        className="flex items-center justify-center h-24 text-center"
        style={{ color: ADOPTION_COLORS.TEXT_SECONDARY }}
      >
        {t('receiver:noPartNumberData')}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex gap-2">
        <Input
          placeholder={t('receiver:searchByExactSku')}
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={e => handleSearchValueChange(e.target.value)}
          style={{ flex: 1 }}
          size="small"
        />
        {searchValue.trim() && (
          <Button
            icon={<CloseOutlined />}
            onClick={handleClearSearch}
            size="small"
            type="default"
            title={t('common:clear')}
          />
        )}
      </div>

      <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
        <div className="flex justify-between">
          <span>
            {searchValue.trim() ? t('receiver:searchResults') : t('receiver:showingResults')}
          </span>
          <span className="font-semibold">
            {currentPagination.total} {t('receiver:partNumbers')}
          </span>
        </div>
      </div>

      <Table
        dataSource={data || []}
        columns={columns}
        loading={loading}
        pagination={{
          current: currentPagination.page,
          pageSize: currentPagination.limit,
          total: currentPagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ${t('receiver:items')}`,
          size: 'small',
          onChange: (page, pageSize) => {
            if (onSearch) {
              onSearch({
                page,
                limit: pageSize || currentPagination.limit,
                ...(searchValue.trim() && { search: searchValue.trim() }),
              });
            }
          },
          onShowSizeChange: (current, size) => {
            if (onSearch) {
              onSearch({
                page: 1,
                limit: size,
                ...(searchValue.trim() && { search: searchValue.trim() }),
              });
            }
          },
        }}
        onChange={onTableChange}
        size="small"
        rowKey={record => `${record.brand}-${record.sku}`}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (() => {
            if (searchValue.trim()) {
              return t('receiver:noMatchingSkus');
            }
            return currentPagination.total === 0
              ? t('receiver:noPartNumberData')
              : t('receiver:noMatchingSkus');
          })(),
        }}
      />
    </div>
  );
};

export default PartNumberBreakdown;
